import { computed, defineComponent, inject, onBeforeUnmount, onMounted, ref, watch } from '@vue/composition-api';
import { isEqual, startCase } from 'lodash';
import SegmentationChart from 'components/charts/SegmentationChart/SegmentationChart.vue';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import WidgetFrame from 'components/widgets/WidgetFrame/WidgetFrame.vue';
import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import icon from 'assets/img/dashboards/dash-queries.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import ProjectAPI from 'src/api/project';
import { getHeader, minMaxValues, sortRows, payloadDataToRows, payloadDataToCSVExport, payloadToData, toolTipData } from './ThemesWidget.utils';
import { makeMenu } from './ThemesWidget.menu';
import { makeRequirements } from "components/DataWidgets/ThemesWidget/ThemesWidget.requirements";
import DataToolTip from '../DataToolTip/DataToolTip.vue';
import { makeBarChartSlide } from '../DataWidgetUtils';
import WidgetMessagePanel from 'components/widgets/WidgetMessagePanel/WidgetMessagePanel.vue';
const ThemesWidget = defineComponent({
    components: {
        WidgetFrame,
        WidgetMenu,
        SegmentationChart,
        DownloadExportButton,
        DataToolTip,
        WidgetMessagePanel,
    },
    props: {
        /** route to navigate to when row is clicked on */
        toQueryRoute: { type: Object, required: false, default: null },
        toConceptRoute: { type: Object, required: false, default: null },
        /** for export filename */
        exportName: { type: String, required: false, default: '' },
        /** is this DataWidget in zoomed mode?  */
        isZoomed: { type: Boolean, required: false, default: false },
        /** route object for zoom button */
        zoomToRoute: { type: Object, required: false, default: null },
        /** data to render */
        data: { type: Object, required: false, default: null },
        /** explanatory text to display at the bottom of the widget **/
        footerText: { type: String, required: false, default: '' },
        /** show developer tools in widget */
        devMode: { type: Boolean, required: false, default: false },
        /** list of queries for the chart */
        queries: { type: Array, required: false, default: () => [] },
        /** list of concepts queries for the chart */
        concepts: { type: Array, required: false, default: () => [] },
        /** if filters have been applied to the dashboard, then we can calculate
         * the expected values for the data */
        isFiltered: { type: Boolean, required: false, default: false },
        /** fields for menu */
        segmentFields: { type: Array, required: false, default: () => [] },
        /** does this data contain NPS? */
        hasNps: { type: Boolean, required: false, default: false },
        /** does this data contain sentiment? */
        hasSentiment: { type: Boolean, required: false, default: false },
        /** does this data contain numeric fields? */
        hasNumericFields: { type: Boolean, default: false, required: false },
        /** widget banner to display */
        banner: { type: Object, default: () => null, required: false },
        /** Add a skeleton mask (used when reloading state between dashboards) */
        masked: { type: Boolean, required: false, default: false },
        config: { type: Object, required: false, default: null },
        themeGroups: { type: Array, required: false, default: () => [] },
    },
    setup(props, { emit }) {
        const analytics = inject('analytics');
        const featureFlags = inject('featureFlags');
        const root = ref(null);
        const wrapper = ref(null);
        const selectedData = ref('Themes');
        const allowShowExpected = ref(true);
        const selectedDisplay = ref('Frequency');
        const validOptions = ref(true);
        const sortBy = ref(2);
        const ascendingSort = ref(false);
        const clickedRowIndex = ref(null);
        const hoveredRowIndex = ref(null);
        const width = ref(300);
        const seriesLabels = computed(() => {
            // Theme Groups are prefixed with 'group_' to avoid conflicts with Themes
            const labels = props.themeGroups.map((g) => [g.name, g.name.replace(/^group_/, '')]);
            return Object.fromEntries(labels);
        });
        const payload = computed(() => {
            return {
                overall: props.data?.overall?.data?.payload,
                filtered: props.data?.filtered?.data?.payload,
                phrases: props.data?.phrases?.data?.payload,
            };
        });
        const phrases = computed(() => {
            return payload.value.phrases ?? [];
        });
        const topPhrasesAsQueries = computed(() => {
            // copy the concepts and sort them as expected here:
            // returning with the same Type as a regular query
            if (phrases.value === undefined)
                return [];
            return phrases.value
                .slice()
                // sort by frequency (not by name)
                .sort((a, b) => a.count_phrase - b.count_phrase)
                // translate top concepts into "queries"
                .map((c) => ({
                name: c.phrase_text,
                query_value: {
                    type: "match_any",
                    includes: [{
                            type: 'text',
                            value: c.phrase_text,
                        }]
                }
            }));
        });
        const themesOrConceptsQueries = computed(() => {
            if (selectedData.value === 'Themes') {
                return props.queries;
            }
            else if (selectedData.value === 'Theme Groups') {
                return props.themeGroups;
            }
            else if (selectedData.value === 'Top Concepts') {
                return props.concepts;
            }
            else if (selectedData.value === 'Top Phrases') {
                return topPhrasesAsQueries.value;
            }
            else {
                throw new Error(`Unknown data type ${selectedData.value}`);
            }
        });
        /* To be able to show expected, the option must be turned on AND there
         * must be an overall and filtered data set to support the comparison of
         * overall vs expected
         * */
        const showingExpected = computed(() => {
            return allowShowExpected.value && props.isFiltered;
        });
        const payloadData = computed(() => {
            return payloadToData(payload.value, themesOrConceptsQueries.value, showingExpected.value);
        });
        const selectedDataLabel = computed(() => {
            if (selectedData.value === 'Themes') {
                return 'Theme';
            }
            else if (selectedData.value === 'Theme Groups') {
                return 'Theme Group';
            }
            else if (selectedData.value === 'Top Concepts') {
                return 'Concept';
            }
            else if (selectedData.value === 'Top Phrases') {
                return 'Phrase';
            }
            else {
                throw new Error(`Unknown data type ${selectedData.value}`);
            }
        });
        const numericalFields = computed(() => {
            return props.segmentFields.reduce((list, field) => field.type === ProjectAPI.COLUMN_LABELED_TYPES.get('NUMBER') ? list.concat(field.name) : list, []);
        });
        const isLoading = computed(() => {
            if (!validOptions.value)
                return true;
            return props.data?.filtered?.status === 'fetching' || props.data?.overall?.status === 'fetching';
        });
        const rowsForChart = computed(() => {
            if (isLoading.value || !payload.value)
                return [];
            return sortRows(payloadDataToRows(payloadData.value, themesOrConceptsQueries.value, selectedDisplay.value, showingExpected.value, sortBy.value), sortBy.value, ascendingSort.value);
        });
        const range = computed(() => {
            // min/max values are rounded (down for min, up for max) to next integer)
            const minMax = minMaxValues(rowsForChart.value, selectedDisplay.value, sortBy.value, showingExpected.value, 1);
            return minMax ?? { min: 0, max: 0 };
        });
        const hoverTheme = computed(() => {
            if (hoveredRowIndex.value == null)
                return null;
            const chartRow = rowsForChart.value[hoveredRowIndex.value];
            if (!chartRow)
                return null;
            let name = chartRow?.label ?? '';
            const dataItem = payloadData.value[name];
            if (!dataItem)
                return null;
            name = seriesLabels.value[name] ?? name;
            return toolTipData(name, dataItem, chartRow, selectedDisplay.value, selectedDataLabel.value, props.hasNps, props.hasSentiment, showingExpected.value);
        });
        const interactionLabel = computed(() => {
            return rowsForChart.value.map((r) => r.label ?? '');
        });
        const validatedOptions = computed(() => {
            let display = props.config?.options?.display;
            let data = props.config?.options?.data;
            let sortBy = props.config?.options?.sortBy ?? 'Frequency (%)';
            let ascendingSort = !!props.config?.options?.ascendingSort;
            let allowShowExpected = !!(props.config?.options?.allowShowExpected ?? true);
            // if the setting is true and it's possible because both the overall
            // data set and the filtered data set exist
            let willShowExpected = allowShowExpected && props.isFiltered;
            // validate display
            if (!display ||
                (display === `nps_` && !props.hasNps) ||
                (display?.endsWith('Sentiment') && !props.hasSentiment) ||
                (numericalFields.value.includes(display) && !props.hasNumericFields) ||
                (willShowExpected && display === 'Frequency') ||
                (!willShowExpected && ['Frequency (%)', 'Frequency (#)'].includes(display))) {
                display = willShowExpected ? 'Frequency (%)' : 'Frequency';
            }
            if (!willShowExpected && display.startsWith('Impact on') && display.endsWith('Sentiment')) {
                display = display.slice(10);
            }
            // validate data
            if (!data) {
                data = 'Themes';
            }
            if (data === 'Themes' && props.queries.length === 0) {
                data = 'Top Concepts';
            }
            if (data === 'Theme Groups' && props.themeGroups.length === 0) {
                data = 'Top Concepts';
            }
            // validate sortBy
            if (typeof sortBy !== 'number' ||
                sortBy < 0 ||
                (willShowExpected && sortBy > 3) ||
                (!willShowExpected && sortBy > 2)) {
                sortBy = 2;
            }
            return { display, data, sortBy, ascendingSort, allowShowExpected };
        });
        const headings = computed(() => {
            return getHeader(selectedDataLabel.value, selectedDisplay.value, sortBy.value, ascendingSort.value, showingExpected.value);
        });
        const hasErrored = computed(() => {
            return !!props.data?.filtered?.error ?? !!props.data?.overall?.error;
        });
        const hasQueries = computed(() => {
            return props.queries?.length > 0;
        });
        const hasThemeGroups = computed(() => {
            return props.themeGroups?.length > 0;
        });
        const NpsField = computed(() => {
            if (!props.hasNps)
                return null;
            const field = props.segmentFields.find((f) => f.type === ProjectAPI.COLUMN_LABELED_TYPES.get('NPS'));
            return field ? field.name : null;
        });
        const legends = computed(() => ({
            'Frequency': ['Frequency (% of records)', 'Frequency (# of records)', 'Frequency (% of records)'],
            'nps_': ['NPS', 'Impact on Overall NPS', 'NPS'],
            'Positive Sentiment': ['Positive Sentiment', 'Impact on Overall Positive Sentiment', 'Positive Sentiment'],
            'Negative Sentiment': ['Negative Sentiment', 'Impact on Overall Negative Sentiment', 'Negative Sentiment'],
            'Neutral Sentiment': ['Neutral Sentiment', 'Impact on Overall Neutral Sentiment', 'Neutral Sentiment'],
            'Mixed Sentiment': ['Mixed Sentiment', 'Impact on Overall Mixed Sentiment', 'Mixed Sentiment'],
            '_': [
                startCase(selectedDisplay.value),
                `Impact on Overall ${startCase(selectedDisplay.value)}`,
                startCase(selectedDisplay.value),
            ],
        }));
        const legend = computed(() => {
            if (showingExpected.value)
                return [
                    { label: 'Observed Value', color: '#11ACDF' },
                    { label: 'Expected Value', color: '#8064AA' },
                ];
            const legend = legends.value[selectedDisplay.value];
            return [
                {
                    label: legend ? legend[sortBy.value] : legends.value['_'][sortBy.value],
                    color: '#11ACDF',
                }
            ];
        });
        const menus = computed(() => {
            return makeMenu(hasQueries.value, selectedData.value, props.hasNps, NpsField.value, selectedDisplay.value, props.hasSentiment, props.hasNumericFields, numericalFields.value, props.isZoomed, showingExpected.value, hasThemeGroups.value, featureFlags);
        });
        const getCsvData = () => {
            return payloadDataToCSVExport(payloadData.value, selectedDisplay.value, selectedDataLabel.value, themesOrConceptsQueries.value, showingExpected.value, props.hasNps, props.hasSentiment, props.hasNumericFields, numericalFields.value);
        };
        const getChartEl = () => {
            return root.value?.$el.querySelector('div.content');
        };
        const getSvgExportConfig = () => {
            const bb = getChartEl()?.getBoundingClientRect();
            if (!bb)
                return;
            return {
                dims: { height: bb.height, width: bb.width },
                padding: 20,
                css: `
          svg {
            top: 0;
            left: 0;
          }
          .chart {
            background-color: #fff;
            cursor: default;
          }
          text.first {
            text-anchor: start;
          }
          text.end {
            text-anchor: end;
          }
          g.row rect.bar {
            fill: #11ACDF;
          }
          rect.background {
            fill: #F1F1F1;
          }
          rect.indicator {
            fill: #8064AA;
          }
          rect.hover  {
            cursor: pointer;
            fill-opacity: 0;
          }
          text.hover {
            fill: #068CCC;
          }
          text.sorted {
            fill: #068CCC;
            font-weight: bold;
          }
          g.header > g.clickable > text.sorted {
            fill: #383838;
          }
          g.header .clickable {
            cursor: pointer;
            user-select: none;
          }
          .header text {
            fill: #95A6AC;
            font-size: 12px;
            text-transform: uppercase;
            font-weight: bold;
          }
          rect.legend-icon-one {
            fill: #11ACDF;
          }
          rect.legend-icon-two {
            fill: #8064AA;
          }
          .legend {
            cursor: default;
          }
          .up-down > path {
            fill: #95a6ac;
            fill-opacity: 1;
          }
          .up-down > path.active {
            fill: #068ccc;
          }
        `
            };
        };
        const fetchData = (force = false) => {
            if (!selectedData.value)
                return;
            let numericSelected = null;
            if (selectedDisplay.value.startsWith('__avg__'))
                numericSelected = [selectedDisplay.value.slice(7)];
            if (selectedDisplay.value.startsWith('__impact_on_avg__'))
                numericSelected = [selectedDisplay.value.slice(17)];
            const requirements = makeRequirements(props.hasNps, props.hasSentiment, numericSelected, themesOrConceptsQueries.value, selectedData.value === 'Top Concepts' ? 25 : false);
            emit('requires-phrases', 'themes-concepts__phrases', {
                limit: props.isZoomed ? 100 : 25,
                min_concept_freq: 5,
                // If no queries are given, no NPMI will be calculated,
                // but we'll still get the top phrases by frequency. So the same
                // backend endpoint serves "Top Phrases" (on the Overview Dashboard)
                // as well as "Key Phrases" (on the Query Dashboard). In the former
                // case, as below, no queries are given. In the latter case, queries
                // are given, and thus the NPMI available in the output are with
                // respect to those queries.
                queries: [],
                sort_asc: false,
                sort_field: "count_pair",
            }, force, true // ensure this request has filters applied
            );
            emit('requires', 'themes-concepts__overall', requirements, force, false);
            emit('requires', 'themes-concepts__filtered', requirements, force, true // ensure this request has filters applied
            );
        };
        const handleRowClicked = (index) => {
            if (index === clickedRowIndex.value) {
                clickedRowIndex.value = null;
            }
            else {
                clickedRowIndex.value = index;
            }
        };
        const navigateTo = (index) => {
            if (!selectedData || index === null)
                return;
            const label = rowsForChart.value[index].label;
            switch (selectedData.value) {
                case 'Themes': {
                    emit('go-to-theme', label);
                    break;
                }
                case 'Top Concepts': {
                    emit('go-to-concept', label);
                    break;
                }
                case 'Top Phrases': {
                    emit('go-to-concept', label);
                    break;
                }
                default:
                    return;
            }
        };
        const setSelectedDisplay = (field, fromConfig) => {
            let fieldname = field[1];
            selectedDisplay.value = fieldname;
            if (!fromConfig) { // don't track programmatic setting of this option
                analytics?.track.themesWidget.changeDisplay(field[0], fieldname);
            }
        };
        const updateConfig = () => {
            const options = {
                ascendingSort: ascendingSort.value,
                display: selectedDisplay.value,
                data: selectedData.value,
                sortBy: sortBy.value,
                allowShowExpected: allowShowExpected.value,
            };
            const updated = Object.assign({}, props.config, { options });
            emit('config-changed', updated);
        };
        const setSelectedData = (field, fromConfig) => {
            let fieldname = field[1];
            selectedData.value = fieldname;
            if (!fromConfig) { // don't track programmatic setting of this option
                analytics?.track.themesWidget.changeData(field[0], fieldname);
            }
        };
        const setSelection = (menuName, field, fromConfig) => {
            if (menuName === 'Display') {
                setSelectedDisplay(field, fromConfig);
            }
            else if (menuName === 'Data') {
                setSelectedData(field, fromConfig);
            }
            if (!fromConfig) {
                updateConfig();
                fetchData();
            }
        };
        const setSorting = (column, ascending = null, fromConfig) => {
            if (ascending === null) {
                ascendingSort.value = (sortBy.value === column) ? !ascendingSort.value : ascendingSort.value;
            }
            else {
                ascendingSort.value = ascending;
            }
            sortBy.value = column;
            if (!fromConfig) { // don't track programmatic setting of this option
                updateConfig();
                analytics?.track.themesWidget.changeSort(headings.value[column].label);
            }
        };
        const setShowExpected = (showExpected, fromConfig) => {
            allowShowExpected.value = showExpected;
            if (!fromConfig) { // don't track programmatic setting of this option
                updateConfig();
                analytics?.track.themesWidget.changeShowExpected(showExpected);
            }
        };
        const setOptionsFromConfig = () => {
            setSelection('Display', [null, validatedOptions.value.display], true);
            setSelection('Data', [null, validatedOptions.value.data], true);
            setSorting(validatedOptions.value.sortBy, validatedOptions.value.ascendingSort, true);
            setShowExpected(validatedOptions.value.allowShowExpected, true);
        };
        const clickedOutside = (e) => {
            if (clickedRowIndex.value === null)
                return;
            const contained = wrapper.value?.contains(e.target);
            if (!contained) {
                clickedRowIndex.value = null;
            }
        };
        const setHoverRowIndex = (index) => {
            hoveredRowIndex.value = index;
        };
        const refresh = () => {
            window.location.reload();
        };
        const reload = () => {
            fetchData(true);
        };
        const contact = () => {
            try {
                window.Intercom('show');
            }
            catch (e) {
                console.warn('intercom show failed');
            }
        };
        const setChartDimensions = (w) => {
            width.value = w;
        };
        const makePptSlide = (pptx) => {
            const slide = pptx.addSlide();
            let column = sortBy.value - 1;
            if (column < 0)
                column = 0;
            const legend = legends.value[selectedDisplay.value] ?? legends.value['_'];
            makeBarChartSlide(pptx, slide, [{
                    name: 'Themes, Concepts & Phrases',
                    labels: rowsForChart.value.map((r) => r.label ?? ''),
                    values: rowsForChart.value.map((r) => r.columns[column].value),
                }], `${props.exportName} - Themes, Concepts & Phrases`, legend.slice(1)[column], selectedData.value);
        };
        watch(() => props.config, () => {
            setOptionsFromConfig();
        }, {
            deep: true,
        });
        watch(() => props.queries, (new_queries) => {
            if (new_queries.length === 0)
                selectedData.value = 'Top Concepts';
            setOptionsFromConfig();
            fetchData();
        }, {
            deep: true,
        });
        watch(phrases, () => {
            if (!hasErrored.value) {
                fetchData();
            }
        });
        watch(() => props.isFiltered, () => {
            setOptionsFromConfig();
            fetchData();
        }, {
            deep: true,
        });
        watch(() => props.data, (newVal, oldVal) => {
            if (!isEqual(oldVal, newVal)) {
                clickedRowIndex.value = null;
            }
        }, {
            deep: true,
        });
        onMounted(() => {
            setOptionsFromConfig();
            fetchData();
            if (typeof window !== 'undefined') {
                document.addEventListener('click', clickedOutside);
            }
        });
        onBeforeUnmount(() => {
            if (typeof window !== 'undefined') {
                document.removeEventListener('click', clickedOutside);
            }
        });
        return {
            getSvgExportConfig,
            getCsvData,
            getChartEl,
            root,
            icon,
            errorIcon,
            reload,
            refresh,
            contact,
            range,
            handleRowClicked,
            hoverTheme,
            navigateTo,
            interactionLabel,
            hasErrored,
            wrapper,
            isLoading,
            setChartDimensions,
            allowShowExpected,
            setShowExpected,
            menus,
            setSelection,
            rowsForChart,
            headings,
            legend,
            width,
            showingExpected,
            setSorting,
            setHoverRowIndex,
            clickedRowIndex,
            makePptSlide,
            seriesLabels,
            selectedData,
            userErrors: null,
        };
    },
});
export default ThemesWidget;
